import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createJournalEntries,
  deleteJournalEntry,
  deleteJournalEntriesByType,
  getJETransactions,
  getJournalEntries,
  getJournalEntryTypes,
} from './journalEntriesThunks';
import { JournalEntryTypes, TradeData } from 'pages/JournalEntries/types';

interface GeneralState {
  jeData: any | null;
  jeTypes: JournalEntryTypes[];
  jeTransactionIds: number[];
  jeTransactionIdsLoading: boolean;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  errorMessage: string | null;
}

interface JournalEntriesState {
  journalEntries: GeneralState;
}

const initialJournalEntriesState: JournalEntriesState = {
  journalEntries: {
    jeData: null,
    jeTypes: [],
    jeTransactionIds: [],
    jeTransactionIdsLoading: false,
    loading: false,
    error: null,
    successMessage: null,
    errorMessage: null,
  },
};

const journalEntriesSlice = createSlice({
  name: 'journalEntries',
  initialState: initialJournalEntriesState,
  reducers: {
    clearJournalEntriesState(state) {
      state.journalEntries = {
        jeData: null,
        jeTypes: [],
        jeTransactionIds: [],
        jeTransactionIdsLoading: false,
        loading: false,
        error: null,
        successMessage: null,
        errorMessage: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Journal Entries
      .addCase(getJournalEntries.pending, (state) => {
        state.journalEntries.loading = true;
        state.journalEntries.error = null;
        state.journalEntries.successMessage = null;
        state.journalEntries.errorMessage = null;
      })
      .addCase(
        getJournalEntries.fulfilled,
        (state, action: PayloadAction<TradeData>) => {
          state.journalEntries.loading = false;
          state.journalEntries.jeData = action.payload;
          state.journalEntries.successMessage =
            'Journal entries fetched successfully';
          state.journalEntries.error = null;
          state.journalEntries.errorMessage = null;
        },
      )
      .addCase(getJournalEntries.rejected, (state, action) => {
        state.journalEntries.loading = false;
        state.journalEntries.error =
          action.payload || 'Failed to fetch journal entries';
        state.journalEntries.errorMessage =
          action.payload || 'Failed to fetch journal entries';
        state.journalEntries.jeData = null;
        state.journalEntries.successMessage = null;
      })

      // create Jounral Entry
      .addCase(createJournalEntries.pending, (state) => {
        state.journalEntries.loading = true;
        state.journalEntries.error = null;
        state.journalEntries.successMessage = null;
        state.journalEntries.errorMessage = null;
      })
      .addCase(
        createJournalEntries.fulfilled,
        (state, action: PayloadAction<TradeData>) => {
          state.journalEntries.loading = false;
          state.journalEntries.jeData = action.payload;
        },
      )
      .addCase(createJournalEntries.rejected, (state, action) => {
        state.journalEntries.loading = false;
        state.journalEntries.error =
          action.payload || 'Failed to create journal entry';
        state.journalEntries.errorMessage =
          'Failed to create journal entry' + action.payload;
        state.journalEntries.jeData = null;
        state.journalEntries.successMessage = null;
      })

      //delete JE
      .addCase(deleteJournalEntry.pending, (state) => {
        state.journalEntries.loading = true;
      })
      .addCase(
        deleteJournalEntry.fulfilled,
        (state, action: PayloadAction<TradeData>) => {
          state.journalEntries.loading = false;
          state.journalEntries.jeData = action.payload;
        },
      )
      .addCase(deleteJournalEntry.rejected, (state) => {
        state.journalEntries.loading = false;
      })

      //delete JE by type
      .addCase(deleteJournalEntriesByType.pending, (state) => {
        state.journalEntries.loading = true;
        state.journalEntries.error = null;
      })
      .addCase(
        deleteJournalEntriesByType.fulfilled,
        (state, action: PayloadAction<TradeData>) => {
          state.journalEntries.loading = false;
          state.journalEntries.jeData = action.payload;
          state.journalEntries.error = null;
        },
      )
      .addCase(deleteJournalEntriesByType.rejected, (state, action) => {
        state.journalEntries.loading = false;
        state.journalEntries.error =
          action.payload || 'Failed to delete journal entries by type';
      })

      // je transactions (trade IDs for the selection dropdown)
      .addCase(getJETransactions.pending, (state) => {
        state.journalEntries.jeTransactionIdsLoading = true;
      })
      .addCase(getJETransactions.fulfilled, (state, action) => {
        state.journalEntries.jeTransactionIdsLoading = false;
        state.journalEntries.jeTransactionIds = action.payload;
      })
      .addCase(getJETransactions.rejected, (state) => {
        state.journalEntries.jeTransactionIdsLoading = false;
        state.journalEntries.jeTransactionIds = [];
      })

      // je types
      .addCase(getJournalEntryTypes.pending, (state) => {
        state.journalEntries.jeTypes = [];
      })
      .addCase(getJournalEntryTypes.fulfilled, (state, action) => {
        state.journalEntries.jeTypes = action.payload;
      })
      .addCase(getJournalEntryTypes.rejected, (state, action) => {
        state.journalEntries.jeTypes = [];
      });
  },
});

export const { clearJournalEntriesState } = journalEntriesSlice.actions;

export default journalEntriesSlice.reducer;
